import { useTranslation } from 'react-i18next'

import { Box, BoxProps, Flex, Icon, Skeleton } from '@chakra-ui/react'

import type { ColorScheme } from '@hivewatch/dark'

import { AssociatedDevices } from '@/components/icons'

import { TailgateBox } from '../types/types'
import { getBoxInput, getBoxInputDefaultOrder } from '../utils/utils'
import { getGridColours } from './GridCoordsBox.styles'

interface GridCoordsBoxIProps {
  box: TailgateBox
  title: string
  subTitle: string
  backgroundColour: string
  colorScheme?: ColorScheme
  iconColour: string
  isValid: boolean
  loading?: boolean
  tone: 'error' | 'success'
}

export const GridCoordsBox = ({
  box,
  title,
  subTitle,
  backgroundColour,
  colorScheme,
  iconColour,
  isValid,
  loading,
  tone,
}: GridCoordsBoxIProps) => {
  const { t } = useTranslation()
  const isIncomplete = box?.corners?.length < 4
  const colours = getGridColours({
    backgroundColour,
    colorScheme,
    iconColour,
    tone,
  })

  // If the box is incomplete then populate topLeft, topRight, bottomRight, bottomLeft in order
  // regardless of their relative position.
  //
  // Once the box is complete use algorithm to detect correct labels.
  const { topLeft, topRight, bottomLeft, bottomRight } = isIncomplete
    ? getBoxInputDefaultOrder(box)
    : getBoxInput(box)

  return (
    <Flex
      backgroundColor={colours.background}
      borderRadius={colorScheme ? 'none' : '0.375rem'}
      data-testid='floorPlanPage_modal_gridCoorsBox'
      direction='column'
      m={5}
      minW='400px'
      mr={0}
      mt={0}
      p={5}
    >
      <Flex alignItems='center' direction='row' flex={1}>
        <Icon
          as={AssociatedDevices}
          boxSize='14px'
          strokeColor={colours.icon}
        />
        <Box color={colours.title} fontSize='12px' fontWeight='bold' ml={2}>
          {title}
        </Box>
      </Flex>
      <Box
        color={colours.descriptionText}
        fontSize='12px'
        fontWeight='medium'
        mt={1}
      >
        {subTitle}
      </Box>

      {!isValid && (
        <Box
          color={colours.primaryText}
          fontSize='14px'
          fontWeight='bold'
          mt={1}
        >
          {t('tailgate:coords_box.invalid_box_points')}
        </Box>
      )}

      <Flex alignItems='center' flex={1} mt={3}>
        {loading ? (
          <>
            <PointSkeleton
              label={t('tailgate:coords_box.top_left_label')}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
            <PointSkeleton
              label={t('tailgate:coords_box.top_right_label')}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
            <PointSkeleton
              label={t('tailgate:coords_box.bottom_right_label')}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
            <PointSkeleton
              label={t('tailgate:coords_box.bottom_left_label')}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
          </>
        ) : (
          <>
            <PointBox
              label={t('tailgate:coords_box.top_left_label')}
              mr={8}
              point={topLeft}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
            <PointBox
              label={t('tailgate:coords_box.top_right_label')}
              mr={8}
              point={topRight}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
            <PointBox
              label={t('tailgate:coords_box.bottom_right_label')}
              mr={8}
              point={bottomRight}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
            <PointBox
              label={t('tailgate:coords_box.bottom_left_label')}
              point={bottomLeft}
              primaryTextColour={colours.primaryText}
              secondaryTextColour={colours.secondaryText}
            />
          </>
        )}
      </Flex>
    </Flex>
  )
}

interface PointBoxIProps extends BoxProps {
  point: { x: number; y: number }
  label: string
  primaryTextColour: string
  secondaryTextColour: string
}

export const PointBox = ({
  point,
  label,
  primaryTextColour,
  secondaryTextColour,
  ...rest
}: PointBoxIProps) => {
  const x = point?.x ? Math.round(point?.x) : 0
  const y = point?.y ? Math.round(point?.y) : 0
  return (
    <Box {...rest}>
      <Box
        color={primaryTextColour}
        fontSize='14px'
        fontWeight='medium'
        minH='25px'
      >
        {`(${x}, ${y})`}
      </Box>
      <Box color={secondaryTextColour} fontSize='12px' fontWeight='medium'>
        {label}
      </Box>
    </Box>
  )
}

interface PointSkeletonIProps {
  label: string
  primaryTextColour: string
  secondaryTextColour: string
}

export const PointSkeleton = ({
  label,
  primaryTextColour,
  secondaryTextColour,
}: PointSkeletonIProps) => {
  return (
    <Box mr={8}>
      <Skeleton
        color={primaryTextColour}
        fontWeight='medium'
        h='25px'
        mb={2}
        w='40px'
      />
      <Box color={secondaryTextColour} fontSize='12px' fontWeight='medium'>
        {label}
      </Box>
    </Box>
  )
}
